import { useMemo } from 'react';
import { useCompany } from '@/hooks/useCompany';
import useCompanyStatementsAPIFormatted from '@/hooks/useCompanyStatementsAPIFormatted';
import {
  formatterCurrencyTruncated,
  formatterFactor,
} from '@/utilities/format';
import { toTruncated } from '@/utilities/formatting';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';
import { getShortTermLiquidityOutcome } from './utils';

type KeyMetric = {
  title: string;
  value: string;
  tooltip?: string | null;
};

type KeyMetrics = [KeyMetric] | [KeyMetric, KeyMetric];

export const useBankMetrics = (canonicalURL: string) => {
  const { data: statements } = useCompanyStatementsAPIFormatted({
    canonicalURL,
  });
  const { HasAppropriateBadLoanAllowance, HasPrimarilyDepositFunding } =
    statements || {};

  const { data } = useCompany(canonicalURL);
  const reportingCurrencyISO =
    data?.data.analysis?.data.extended.data.raw_data.data.currency_info
      .reporting_currency_iso || 'USD';
  const {
    assets_equity_ratio: assetsEquityRatio,
    cash_st_investments: totalCash,
    current_assets: currentAssets,
    total_current_liab: currentLiabilities,
    industry_analysis: {
      total_deposits: totalDeposits,
      non_perf_loans_total_loans: totalNonPerfLoans,
      net_int_margin: netInterestMargin,
    },
  } = data?.data.analysis?.data.extended.data.analysis.health || {
    industry_analysis: {},
  };

  const loanToDepositValue = HasPrimarilyDepositFunding?.value;
  const badLoanValue = HasAppropriateBadLoanAllowance?.value;

  const term = useGlossaryTerms();

  const keyMetrics: KeyMetrics = useMemo(
    () => [
      {
        title: 'Asset to equity ratio',
        value: formatterFactor(assetsEquityRatio),
        tooltip: term('assets to equity ratio'),
      },
      {
        title: 'Net interest margin',
        value:
          typeof netInterestMargin === 'number'
            ? `${toTruncated(netInterestMargin, 1)}%`
            : 'n/a',
        tooltip: term('net interest margin'),
      },
    ],
    [assetsEquityRatio, netInterestMargin, term]
  );

  const metrics = useMemo(() => {
    const loanToDepositOutcome = loanToDepositValue ? 'Appropriate' : 'High';
    const badLoanOutcome = badLoanValue ? 'Sufficient' : 'Low';
    const shortTermLiquidityOutcome = getShortTermLiquidityOutcome(
      currentAssets,
      currentLiabilities
    );
    return [
      {
        title: 'Total deposits',
        value:
          typeof totalDeposits === 'number'
            ? formatterCurrencyTruncated(
                totalDeposits * 1_000_000,
                reportingCurrencyISO,
                2
              )
            : 'n/a',
        tooltip: term('total deposits'),
      },
      {
        title: 'Loan to deposit ratio',
        value:
          typeof loanToDepositValue === 'boolean'
            ? loanToDepositOutcome
            : 'n/a',
        tooltip: term('loans to deposits ratio'),
      },
      {
        title: 'Bad loans',
        value:
          typeof totalNonPerfLoans === 'number'
            ? `${toTruncated(totalNonPerfLoans, 1)}%`
            : 'n/a',
        tooltip: term('bad loans'),
      },
      {
        title: 'Allowance for bad loans',
        value: typeof badLoanValue === 'boolean' ? badLoanOutcome : 'n/a',
        tooltip: term('allowance for bad loans'),
      },
      {
        title: 'Current ratio',
        value: shortTermLiquidityOutcome,
        tooltip: term('current ratio'),
      },
      {
        title: 'Cash & equivalents',
        value:
          typeof totalCash === 'number'
            ? formatterCurrencyTruncated(
                totalCash * 1_000_000,
                reportingCurrencyISO,
                2
              )
            : 'n/a',
        tooltip: term('cash'),
      },
    ];
  }, [
    currentAssets,
    currentLiabilities,
    badLoanValue,
    loanToDepositValue,
    reportingCurrencyISO,
    totalCash,
    totalDeposits,
    totalNonPerfLoans,
    term,
  ]);

  return { keyMetrics, metrics };
};
